// src/pages/AdminPanel/Settings.jsx
import React, { useState, useEffect } from 'react';
import { db } from '../../services/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import Swal from 'sweetalert2'; // For notifications
import "./Settings.css";

const Settings = () => {
  const [shopName, setShopName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [shippingRate, setShippingRate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Assuming a single document named 'global' in a 'settings' collection
        const settingsDocRef = doc(db, 'settings', 'global');
        const settingsSnap = await getDoc(settingsDocRef);
        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          setShopName(data.shopName || '');
          setContactEmail(data.contactEmail || '');
          // Convert shippingRate to string for input if stored as number
          setShippingRate(data.shippingRate !== undefined ? data.shippingRate.toString() : '');
        }
      } catch (err) {
        console.error("Error fetching settings:", err);
        Swal.fire({
          title: "Error",
          text: "Failed to load settings.",
          icon: "error",
          background: '#1a202c',
          color: '#e2e8f0',
          customClass: { popup: 'swal2-popup-custom', title: 'swal2-title-custom' }
        });
      } finally {
        setInitialLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!shopName) newErrors.shopName = 'Shop name is required.';
    if (!contactEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      newErrors.contactEmail = 'A valid contact email is required.';
    }
    if (shippingRate === '' || isNaN(shippingRate) || parseFloat(shippingRate) < 0) {
      newErrors.shippingRate = 'A valid shipping rate is required (must be non-negative).';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please fill in all required fields and correct any errors.',
        icon: 'error',
        background: '#1a202c',
        color: '#e2e8f0',
        customClass: { popup: 'swal2-popup-custom', title: 'swal2-title-custom' }
      });
      return;
    }

    setIsLoading(true);
    try {
      const settingsDocRef = doc(db, 'settings', 'global');
      await updateDoc(settingsDocRef, {
        shopName: shopName,
        contactEmail: contactEmail,
        shippingRate: parseFloat(shippingRate), // Ensure it's stored as a number
        updatedAt: new Date().toISOString() // Or use serverTimestamp() if desired
      }, { merge: true }); // Use merge to avoid overwriting other fields

      Swal.fire({
        title: 'Success!',
        text: 'Settings updated successfully.',
        icon: 'success',
        background: '#1a202c',
        color: '#e2e8f0',
        customClass: { popup: 'swal2-popup-custom', title: 'swal2-title-custom' }
      });
    } catch (err) {
      console.error("Error updating settings:", err);
      Swal.fire({
        title: 'Error!',
        text: `Failed to update settings: ${err.message}`,
        icon: 'error',
        background: '#1a202c',
        color: '#e2e8f0',
        customClass: { popup: 'swal2-popup-custom', title: 'swal2-title-custom' }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const InputField = ({ label, name, type = 'text', placeholder, value, onChange, error, ...props }) => (
    <div className="setting-form-group"> {/* Specific class name */}
      <label htmlFor={name} className="setting-form-label">{label}</label> {/* Specific class name */}
      <input
        type={type}
        id={name}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="setting-form-input" /* Specific class name */
        {...props}
      />
      {error && <p className="setting-input-error-message">{error}</p>} {/* Specific class name */}
    </div>
  );

  if (initialLoading) return <div className="text-center py-10 text-gray-300 text-xl">Loading Settings...</div>;

  return (
    <div className="settings-container-main"> {/* Main container for page-level styling */}
      <h2 className="section-title">Store Settings</h2> {/* Consistent title styling */}
      <form onSubmit={handleSubmit} className="settings-form"> {/* Custom class for form */}
        <InputField 
          label="Shop Name" 
          name="shopName" 
          placeholder="e.g., Alex Menswear Co." 
          value={shopName} 
          onChange={(e) => setShopName(e.target.value)} 
          error={errors.shopName} 
          required 
        />
        <InputField 
          label="Contact Email" 
          name="contactEmail" 
          type="email" 
          placeholder="e.g., support@alexmenswear.com" 
          value={contactEmail} 
          onChange={(e) => setContactEmail(e.target.value)} 
          error={errors.contactEmail} 
          required 
        />
        <InputField 
          label="Default Shipping Rate (USD)" 
          name="shippingRate" 
          type="number" 
          placeholder="e.g., 5.99" 
          value={shippingRate} 
          onChange={(e) => setShippingRate(e.target.value)} 
          error={errors.shippingRate} 
          step="0.01" 
          required 
        />
        
        <button 
          type="submit" 
          disabled={isLoading}
          className="setting-submit-button" /* Specific class name */
        >
          {isLoading ? 'Saving Settings...' : 'Save Settings'}
        </button>
      </form>
    </div>
  );
};

export default Settings;
